/**
 * EventCard Component
 *
 * Displays a single parsed SSE event with type badge, timestamp, and collapsible data
 */

import React, { useState } from 'react';
import { ParsedEvent, EventType } from '../../types/session';

interface EventCardProps {
  event: ParsedEvent;
  index: number;
}

const eventTypeStyles: Record<EventType, string> = {
  initialize_event: 'bg-gray-100 text-gray-800 border-gray-300',
  action: 'bg-blue-100 text-blue-800 border-blue-300',
  action_success: 'bg-green-100 text-green-800 border-green-300',
  action_failed: 'bg-red-100 text-red-800 border-red-300',
  final_response: 'bg-purple-100 text-purple-800 border-purple-300',
  error: 'bg-red-100 text-red-800 border-red-300',
};

const eventTypeLabels: Record<EventType, string> = {
  initialize_event: 'Initialize',
  action: 'Action',
  action_success: 'Success',
  action_failed: 'Failed',
  final_response: 'Final Response',
  error: 'Error',
};

export const EventCard: React.FC<EventCardProps> = ({ event, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatRelativeTime = (timestamp?: number) => {
    if (!timestamp) return '';
    const now = Date.now() / 1000;
    const diff = now - timestamp;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  // Type guard: use known styles if event.type matches EventType, otherwise fallback
  const isKnownEventType = (type: string): type is EventType => {
    return type === 'initialize_event' || type === 'action' || type === 'action_success' || type === 'action_failed' || type === 'final_response' || type === 'error';
  };

  const styles = isKnownEventType(event.type)
    ? eventTypeStyles[event.type]
    : 'bg-gray-100 text-gray-800 border-gray-300'; // Default fallback
  const label = isKnownEventType(event.type)
    ? eventTypeLabels[event.type]
    : event.type; // Show raw type for unknown types

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles}`}
            role="status"
            aria-label={`Event type: ${label}`}
          >
            {label}
          </span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded p-1"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {isExpanded ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            )}
          </svg>
        </button>
      </div>

      {/* Timestamp and Token Info */}
      <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
        <div>
          {formatTimestamp(event.timestamp)}
          {event.timestamp && (
            <span className="ml-1 text-gray-500">({formatRelativeTime(event.timestamp)})</span>
          )}
        </div>
        {event.totalTokenCount !== undefined && (
          <div className="text-gray-500">
            {event.totalTokenCount.toLocaleString()} tokens
          </div>
        )}
      </div>

      {/* Event Data Summary */}
      {!isExpanded && (
        <div className="text-sm text-gray-700">
          {event.purpose && (
            <p>{event.purpose}</p>
          )}
          {event.type === 'final_response' && event.final_response && (
            <p className="text-gray-600 truncate">{event.final_response}</p>
          )}
          {event.error_message && (
            <p className="text-red-600">{event.error_message}</p>
          )}
        </div>
      )}

      {/* Expanded Event Data */}
      {isExpanded && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <pre className="text-xs bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto max-h-80 overflow-y-auto">
            <code>{JSON.stringify(event.data, null, 2)}</code>
          </pre>
          {(event.role || event.finishReason) && (
            <div className="mt-2 flex items-center gap-3 text-xs text-gray-600">
              {event.role && (
                <div>
                  <span className="font-medium">Role:</span> {event.role}
                </div>
              )}
              {event.finishReason && (
                <div>
                  <span className="font-medium">Finish:</span> {event.finishReason}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
